import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkEditPayload, Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper, WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportBulkAction } from 'modules/menu/behavioral';
import {
  VatCategories,
  VatFieldValue,
  initialVatFieldValue,
  useVatCategories,
} from 'modules/menu/components/VatCategories';
import { useBulkChangeVatTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClearSelection: () => void;
  onClose: () => void;
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  background-color: ${cssThemedValue('surfaceMain')};
  padding: 0;
  border-radius: 8px;
  overflow-y: visible;
`;

const Footer = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const validationSchema = yup.object({
  vat: yup
    .object({
      categoryCode: yup.string().required(),
      percentage: yup.number().required(),
    })
    .required(),
});

const BulkVatChangeModal = ({ categories, menuId, onClearSelection, onClose, selectedIds }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();
  const trackChangeVat = useBulkChangeVatTracking([...selectedIds]);

  const vatCategories = useVatCategories();

  const handleVatChange = useMutation((payload: BulkEditPayload) => bulkEditItems(menuId, payload), {
    onMutate: () => {
      reportBulkAction(selectedIds.size, 'BULK_CHANGE_VAT');
    },
    onSuccess: (data: MenuContainer, variables: BulkEditPayload) => {
      updateMenuData({ actionType: 'updateMenuContainer', payload: data });
      onClearSelection();
      trackChangeVat(Number(variables.vat_category?.numeric_value || '0'));
      toast.success(<FormattedMessage id="menu.bulkActions.vatChange.successDescription" />);
    },
    onError: () => {
      toast.error(<FormattedMessage id="menu.bulkActions.vatChange.errorDescription" />, {
        duration: Infinity,
      });
    },
    onSettled: () => {
      onClose();
    },
  });

  return (
    <Root isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.vatChange.title" />}
        />
      </ModalHeader>
      <Form<{ vat: VatFieldValue }>
        initialValues={{ vat: initialVatFieldValue(vatCategories.data?.list, undefined) }}
        onSubmit={values => {
          const { vat } = validationSchema.validateSync(values);
          const payload: BulkEditPayload = {
            ...buildBulkActionBasePayload(categories, selectedIds),
            vat_category: {
              category_code: vat.categoryCode,
              numeric_value: String(vat.percentage),
            },
          };

          handleVatChange.mutate(payload);
        }}
        validate={validateYupSchema(validationSchema)}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, pristine, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <ModalContent style={{ padding: 0 }}>
              <FormRow
                horizontalPadding
                label={intl.formatMessage({ id: 'menu-item.vat-label-edit' })}
                labelAlign="center"
              >
                <Field<VatFieldValue> name="vat">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <VatCategories
                          {...input}
                          vatCategories={vatCategories.data?.list || []}
                          disabled={handleVatChange.isLoading || !vatCategories.data}
                          lang={intl.locale}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
            </ModalContent>
            <Footer>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                disabled={pristine || hasValidationErrors || handleVatChange.isLoading || !vatCategories.data}
                loading={handleVatChange.isLoading || vatCategories.isLoading}
                type="submit"
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </Footer>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default BulkVatChangeModal;
